/**
 * RS Customizer — Ombre Blanket
 * Text-based preview renderer for ombre blanket customization.
 * Renders text rows stacked vertically with cycling colors and texture.
 * No SVG path, no GraphQL — config from Liquid endpoint only.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const LIQUID_VIEW = 'metaobject-ombre-blanket';

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _resizeTimer = null;

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) { text += field.value; });
    return text.trim().replace(/_/g, ' ');
  }

  // Selects font family by detecting case in text.
  function _getFontFamily(text) {
    return /[a-z]/.test(text) ? "'MadeLikesScript', sans-serif" : "'AllTheWayToTheSun', sans-serif";
  }

  function _hexToRgb(hex) {
    if (!hex || hex.length < 7) return null;
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16),
    };
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return null;
    const key = variantTitle.toLowerCase();
    let match = null;
    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === key || key.includes(pair[0].toLowerCase())) {
        match = pair[1];
      }
    });
    if (!match) {
      const keys = Object.keys(_backgroundsData);
      if (keys.length > 0) match = _backgroundsData[keys[0]];
    }
    return match;
  }

  // ─── Size lookup ────────────────────────────────────────────────

  function _getCurrentSize() {
    if (!_baseConfig || !_baseConfig.letters_row_dimensions) return null;
    const sizes = Object.keys(_baseConfig.letters_row_dimensions);
    const title = (_currentVariantTitle || '').toLowerCase();
    const match = sizes.find(function (s) { return title.includes(s.toLowerCase()); });
    return match || sizes[0] || null;
  }

  // ─── Canvas management ──────────────────────────────────────────

  function _getCanvas() {
    if (!_injectedSlideId) return null;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;
    let canvas = customizerView.querySelector('.rs-ombre__canvas');
    if (!canvas) {
      canvas = document.createElement('div');
      canvas.className = 'rs-ombre__canvas';
      customizerView.appendChild(canvas);
    }
    return canvas;
  }

  // ─── Text rendering ─────────────────────────────────────────────

  function _renderText() {
    const text = _getMtoText();
    const canvas = _getCanvas();
    if (!canvas) return;

    canvas.querySelectorAll('[data-rs-ombre-row]').forEach(function (el) { el.remove(); });

    if (!text || !_baseConfig) return;

    const currentSize = _getCurrentSize();
    if (!currentSize) return;

    const dimensions = _baseConfig.letters_row_dimensions[currentSize];
    if (!dimensions) return;

    const rowCount = _baseConfig.letters_row_iteration || 8;
    const colors = Object.values(_baseConfig.letters_row_colors || {});
    const yPosition = dimensions.y_position;
    const availableHeightPercent = 100 - 2 * yPosition;
    const fontFamily = _getFontFamily(text);

    const flex = document.createElement('div');
    flex.className = 'rs-ombre__flex';
    flex.setAttribute('data-rs-ombre-row', '');
    flex.style.left = dimensions.x_position + '%';
    flex.style.top = yPosition + '%';
    flex.style.width = dimensions.width + '%';
    flex.style.height = availableHeightPercent + '%';

    // Calculate font size from rendered canvas height.
    const canvasRect = canvas.getBoundingClientRect();
    const flexHeight = (availableHeightPercent / 100) * (canvasRect.height || 400);
    const rowHeight = flexHeight / rowCount;
    const isUppercase = !/[a-z]/.test(text);
    const fontSize = Math.max(isUppercase ? rowHeight * 1.12 : rowHeight, 20);

    for (let i = 0; i < rowCount; i++) {
      const color = colors[i % colors.length] || '#000000';
      const rgb = _hexToRgb(color);

      const row = document.createElement('div');
      row.className = 'rs-ombre__row';

      const span = document.createElement('span');
      span.className = 'rs-ombre__letter';
      span.textContent = text;
      span.style.fontSize = fontSize + 'px';
      span.style.fontFamily = fontFamily;

      if (rgb) {
        span.style.setProperty('--customizer-ombre-blanket-selected-color', color);
        span.style.setProperty('--customizer-ombre-blanket-r', String(rgb.r));
        span.style.setProperty('--customizer-ombre-blanket-g', String(rgb.g));
        span.style.setProperty('--customizer-ombre-blanket-b', String(rgb.b));
      }

      row.appendChild(span);
      flex.appendChild(row);
    }

    canvas.appendChild(flex);
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderText();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderText();
      if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      if (_getMtoText()) _renderText();
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[OmbreBlanket] Data fetch failed:', err);
      return;
    }

    _baseConfig = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};

    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }

    if (_getMtoText()) _renderText();
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
